/**
 * CSV export of the Site Variance ranking — the rows the table shows, in
 * the same order (worst first), one line per site.
 */
import type { ITableColumn, ITableData } from "@/models/table"
import { downloadCsvNew } from "@/utils/download"
import type { TFunction } from "i18next"

import type { VarianceDetailPayload, VarianceSiteRow } from "./variance.types"

interface VarianceExportRow {
  id: string
  rank: number
  site: string
  site_id: string
  site_meta: string
  over_expected_pct: number
  dollars_above_expected: number
  peer_percentile: number | string
  cohort_size: number | string
  site_eui: number
  flagged_by: string
  provenance: string
  data_incomplete: string
}

/** Column key → `neat.variance.export.*` translation key, in CSV order. */
const COLUMN_KEYS: [keyof VarianceExportRow, string][] = [
  ["rank", "rank"],
  ["site", "site"],
  ["site_id", "siteId"],
  ["site_meta", "details"],
  ["over_expected_pct", "overExpectedPct"],
  ["dollars_above_expected", "dollarsAboveExpected"],
  ["peer_percentile", "peerPercentile"],
  ["cohort_size", "cohortSize"],
  ["site_eui", "siteEui"],
  ["flagged_by", "flaggedBy"],
  ["provenance", "source"],
  ["data_incomplete", "gasIncomplete"],
]

const toExportRow = (
  s: VarianceSiteRow,
  i: number,
  t: TFunction
): VarianceExportRow => ({
  id: s.site_id,
  rank: i + 1,
  site: s.site_label,
  site_id: s.site_id,
  site_meta: s.site_meta,
  over_expected_pct: s.over_expected_pct,
  dollars_above_expected: Math.round(s.dollars_above_expected),
  // A cohort size of 0 means no peer benchmark (e.g. no floor area).
  peer_percentile: s.peer.cohort_size ? s.peer.percentile : "",
  cohort_size: s.peer.cohort_size || "",
  site_eui: s.site_eui,
  flagged_by: (s.flagged_by ?? []).join("; "),
  provenance: s.provenance,
  data_incomplete: s.data_incomplete
    ? t("neat.variance.export.yes")
    : t("neat.variance.export.no"),
})

export const varianceRankingTable = (
  sites: readonly VarianceSiteRow[],
  t: TFunction
): ITableData<VarianceExportRow> => ({
  columns: COLUMN_KEYS.map(
    ([key, titleKey], i): ITableColumn<VarianceExportRow> => ({
      id: i,
      key,
      order: i,
      title: t(`neat.variance.export.${titleKey}`),
    })
  ),
  rows: sites.map((s, i) => toExportRow(s, i, t)),
})

export const downloadVarianceRanking = (
  payload: VarianceDetailPayload,
  t: TFunction
): void => {
  const slug = payload.period_label.toLowerCase().replace(/[^a-z0-9]+/g, "-")
  downloadCsvNew(
    varianceRankingTable(payload.sites, t),
    `site-variance-ranking-${slug}`
  )
}
